import { Injectable } from '@angular/core';
import { Dialog, DialogType } from 'src/shared/utils';
import { Observable, of, forkJoin } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class UserNotificationService {
  constructor(private translateService: TranslateService) {}

  /**
   * Obtiene el mensaje traducido o usa el mensaje por defecto
   * @param messageKey - Clave del mensaje a traducir o mensaje directo
   * @param params - Parámetros para la traducción
   */
  private getTranslatedMessage(messageKey: string, params?: Object): Observable<string> {
    return this.translateService.get(messageKey, params).pipe(
      catchError(() => of(messageKey))
    );
  }

  /**
   * Obtiene las traducciones de los botones
   */
  private getButtonTranslations(): Observable<{accept: string, cancel: string}> {
    return forkJoin({
      accept: this.translateService.get('COMMON.BUTTONS.ACCEPT'),
      cancel: this.translateService.get('COMMON.BUTTONS.CANCEL')
    });
  }

  /**
   * Muestra un mensaje de éxito.
   * @param messageKey - Clave del mensaje a traducir o mensaje directo.
   * @param params - Parámetros para la traducción (opcional).
   * @param isHTML - Indica si el mensaje incluye HTML.
   */
  showSuccess(messageKey: string, params?: Object, isHTML: boolean = false): void {
    forkJoin({
      message: this.getTranslatedMessage(messageKey, params),
      buttons: this.getButtonTranslations()
    }).subscribe(({ message, buttons }) => {
      Dialog.showDialog(message, DialogType.success, isHTML, buttons.accept);
    });
  }

  /**
   * Muestra un mensaje de error.
   * @param messageKey - Clave del mensaje a traducir o mensaje directo.
   * @param params - Parámetros para la traducción (opcional).
   * @param isHTML - Indica si el mensaje incluye HTML.
   */
  showError(messageKey: string, params?: Object, isHTML: boolean = false): void {
    forkJoin({
      message: this.getTranslatedMessage(messageKey, params),
      buttons: this.getButtonTranslations()
    }).subscribe(({ message, buttons }) => {
      Dialog.showDialog(message, DialogType.error, isHTML, buttons.accept);
    });
  }

  /**
   * Muestra un mensaje de advertencia.
   * @param messageKey - Clave del mensaje a traducir o mensaje directo.
   * @param params - Parámetros para la traducción (opcional).
   * @param isHTML - Indica si el mensaje incluye HTML.
   */
  showWarning(messageKey: string, params?: Object, isHTML: boolean = false): void {
    forkJoin({
      message: this.getTranslatedMessage(messageKey, params),
      buttons: this.getButtonTranslations()
    }).subscribe(({ message, buttons }) => {
      Dialog.showDialog(message, DialogType.warning, isHTML, buttons.accept);
    });
  }

  /**
   * Muestra un mensaje de información.
   * @param messageKey - Clave del mensaje a traducir o mensaje directo.
   * @param params - Parámetros para la traducción (opcional).
   * @param isHTML - Indica si el mensaje incluye HTML.
   */
  showInformation(messageKey: string, params?: Object, isHTML: boolean = false): void {
    forkJoin({
      message: this.getTranslatedMessage(messageKey, params),
      buttons: this.getButtonTranslations()
    }).subscribe(({ message, buttons }) => {
      Dialog.showDialog(message, DialogType.info, isHTML, buttons.accept);
    });
  }

  /**
   * Muestra un mensaje de pregunta con confirmación.
   * @param messageKey - Clave del mensaje a traducir o mensaje directo.
   * @param params - Parámetros para la traducción (opcional).
   * @param isHTML - Indica si el mensaje incluye HTML.
   * @returns Observable<boolean> - Devuelve true si el usuario confirma.
   */
  showQuestion(messageKey: string, params?: Object, isHTML: boolean = false): Observable<boolean> {
    return new Observable(observer => {
      forkJoin({
        message: this.getTranslatedMessage(messageKey, params),
        buttons: this.getButtonTranslations()
      }).subscribe(({ message, buttons }) => {
        Dialog.showDialog(
          message, 
          DialogType.question, 
          isHTML, 
          buttons.accept, 
          buttons.cancel
        ).subscribe(result => {
          observer.next(result);
          observer.complete();
        });
      });
    });
  }
}